import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Image } from "antd";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import type { HomeLatestViewpoint } from "../../SpaceType";
import { avatarDisplayWord } from "../../../../utils/avatarDisplayWord";
import { resolveAppMediaUrl } from "../../../../utils/resolveAppMediaUrl";
import "../../../../products/viewpoint/ViewpointFeedPage.css";

function importanceClass(imp: string): string {
  if (imp === "urgent") return "lf-vp-imp lf-vp-imp--urgent";
  if (imp === "important") return "lf-vp-imp lf-vp-imp--important";
  return "lf-vp-imp lf-vp-imp--normal";
}

function importanceLabel(imp: string): string {
  if (imp === "urgent") return "精选";
  if (imp === "important") return "重要";
  return "";
}

function displayPublishedCompact(publishTime: string): string {
  const t = publishTime.trim();
  if (!t) return "";
  const d = dayjs(t);
  return d.isValid() ? d.format("M月D日 HH:mm") : t;
}

function lockedViewpointHint(viewpoint: HomeLatestViewpoint): string {
  const name = viewpoint.requiredProductDisplayName?.trim();
  if (name) {
    return `订阅「${name}」后可查看全文`;
  }
  return "订阅观点产品后可查看全文";
}

function nicknameOf(viewpoint: HomeLatestViewpoint): string {
  const nick = viewpoint.publisherNickname?.trim();
  if (nick) return nick;
  const label = viewpoint.publisherLabel?.trim();
  if (label) return label;
  return "用户";
}

function resolveImageUrls(viewpoint: HomeLatestViewpoint): string[] {
  const urls = viewpoint.imageUrls?.map((u) => u.trim()).filter(Boolean) ?? [];
  if (urls.length > 0) {
    return urls;
  }
  const single = viewpoint.imageUrl?.trim();
  return single ? [single] : [];
}

/** 观点最新卡片（样式对齐观点产品 feed，标签不可点击） */
export function SpaceHomeViewpointPreview(props: {
  spaceId: string;
  viewpoint: HomeLatestViewpoint;
  fallbackSpaceProductId?: string | null;
}): JSX.Element {
  const { spaceId, viewpoint, fallbackSpaceProductId } = props;
  const navigate = useNavigate();
  const accessible = viewpoint.contentAccessible;
  const nick = nicknameOf(viewpoint);
  const importance = viewpoint.importance?.trim() || "normal";
  const showImportance = importance === "important" || importance === "urgent";
  const titleText =
    viewpoint.titleMode && viewpoint.title?.trim() ? viewpoint.title.trim() : null;
  const categoryName = viewpoint.categoryName?.trim() || "";
  const imageUrls = resolveImageUrls(viewpoint);
  const targetSpaceProductId =
    viewpoint.spaceProductId?.trim() || fallbackSpaceProductId?.trim() || "";

  const onViewMore = useCallback(() => {
    if (!targetSpaceProductId) {
      return;
    }
    navigate(`/space/${spaceId}/${encodeURIComponent(targetSpaceProductId)}`);
  }, [navigate, spaceId, targetSpaceProductId]);

  return (
    <section className="space-home-feed__section" aria-label="最新观点">
      <div className="space-home-feed__section-head">
        <p className="space-home-feed__section-hint">最新观点</p>
        {targetSpaceProductId ? (
          <button
            type="button"
            className="space-home-feed__section-more"
            onClick={onViewMore}
          >
            查看更多
          </button>
        ) : null}
      </div>
      <article
        className={
          accessible
            ? "lf-vp-card space-home-feed__viewpoint-card"
            : "lf-vp-card space-home-feed__viewpoint-card space-home-feed__viewpoint-card--locked"
        }
      >
        <div className="lf-vp-card__head">
          <EasyAvatar
            shape="square"
            squareRadius={4}
            size={30}
            url={resolveAppMediaUrl(viewpoint.publisherAvatarUrl) || undefined}
            word={avatarDisplayWord(nick, viewpoint.publisherLabel)}
            alt=""
            className="lf-vp-card__avatar"
          />
          <div className="lf-vp-card__main">
            <div className="lf-vp-card__user-line">
              <span className="lf-vp-card__nick">{nick}</span>
              <span className="lf-vp-card__time-inline">
                {displayPublishedCompact(viewpoint.publishTime)}
              </span>
            </div>
            {titleText ? <div className="lf-vp-card__title-text">{titleText}</div> : null}
            <div className="lf-vp-card__body">
              {showImportance ? (
                <span className={importanceClass(importance)}>{importanceLabel(importance)}</span>
              ) : null}
              {accessible ? viewpoint.content : lockedViewpointHint(viewpoint)}
              {accessible && categoryName ? (
                <>
                  {" "}
                  <span className="lf-vp-card__hash-tag space-home-feed__viewpoint-tag">
                    #{categoryName}
                  </span>
                </>
              ) : null}
            </div>
            {imageUrls.length > 0 ? (
              <div className="lf-vp-card__images">
                <Image.PreviewGroup preview={{ destroyOnClose: true }}>
                  <div className="lf-vp-card__images-inner">
                    {imageUrls.map((u, idx) => (
                      <div key={`${idx}-${u}`} className="lf-vp-card__image-cell">
                        <Image src={u} alt="" className="lf-vp-card__image" />
                      </div>
                    ))}
                  </div>
                </Image.PreviewGroup>
              </div>
            ) : null}
          </div>
        </div>
      </article>
    </section>
  );
}
